/**
 * Edit-mode input on the viewer's scroller (native listeners, no React state per event):
 *  - non-passive touchstart/touchmove on page boxes call preventDefault where the page must not
 *    scroll (blocks Scribble, the magnifier and callouts): every touch when fingers draw; when
 *    fingers navigate, the Apple Pencil (touchType 'stylus') and every touch while a pen is down or
 *    inside the palm window (a palm must not slide the page under a stroke);
 *  - Pointer Events drive strokes, highlighter segments, the eraser, selection drags and taps;
 *  - two fingers pan by writing scrollTop/scrollLeft (pinch zoom: the viewer's WebKit gesture /
 *    touch fallback, plus a setZoom fallback when WebKit sent no gesture events) when fingers draw,
 *    or when the first finger was dragging the selection (its handles are touch-action:none, so the
 *    browser would not pan); a second finger always cancels a one-finger drag of the selection;
 *  - palm rejection: touches are ignored while a pen is down and 300 ms after it lifts;
 *  - direct manipulation (routing.ts): with Penni, Yfirstrikun, Texti, Tákn and Velja a tap on an
 *    own mark selects it and the selection is moved / resized by its frame and handles (ink and
 *    highlights in Penni / Yfirstrikun: by the mark itself and its handles); a tap that does not
 *    travel there acts as a tap on the page (except on a handle's visible icon);
 *  - long press (routing.ts armsLongPress / resolveLongPress): with Penni, Yfirstrikun, Texti and
 *    Tákn a press that stays still (gesture.ts longPressSlopPx) for LONG_PRESS_MS on an own mark
 *    (ink and highlights too) drops its stroke / dot / tap, selects the mark and drags it with the
 *    same pointer. This hook only arms and clears the timer: drift, lift, cancel, a second finger,
 *    edit mode ending; a tool change, an open text box or a pointer already lifted elsewhere is
 *    checked when it fires.
 * Wet strokes are drawn into the page overlay's registered SVG elements in requestAnimationFrame.
 */
import { useEffect, type RefObject } from 'react';
import { Q, type AnnotationObject } from '../../shared/annotations';
import type { PageSize } from '../../shared/dto/library';
import { supportsGestureEvents } from '../viewer/usePinchZoom';
import type { PagePoint, ViewerHandle } from '../viewer/types';
import { INTERACTIVE_SELECTOR, pageElement } from './dom';
import {
  clampDelta,
  clientToPage,
  cssToUnits,
  dragScale,
  HANDLE_ICON_RADIUS_PX,
  hitContext,
  HIT_TOLERANCE_PX,
  normToUnits,
  objectBounds,
  pageHeightUnits,
  segmentHitsObject,
  snapHorizontal,
  UNITS_W,
  type Pt,
} from './geometry';
import { isTap, LONG_PRESS_MS, longPressSlopPx, palmRejected, tapMaySelect, type FingerRole, type PenState } from './gesture';
import { FINGER_START_PRESSURE, MAX_RAW_POINTS, outlinePath, penPressure, simulatePressure, type RawPt } from './ink';
import {
  armsLongPress,
  resolveLongPress,
  routePointerDown,
  selectionGrabbedOnMark,
  selectionScalable,
  type DownRoute,
  type SelectionPart,
  type TapAction,
} from './routing';
import type { OverlayStore } from './store';
import type { ToolId } from './tools';

/** Context of a tap: the selection when the pointer went down, and whether it went down on it. */
export interface TapFrom {
  selectedId: string | null;
  onHandle: boolean;
  /** The pointer may select (gesture.ts tapMaySelect). */
  selects: boolean;
}

/** Latest controller state + callbacks, read through a ref by the native handlers. */
export interface EditInputLive {
  tool: ToolId;
  role: FingerRole;
  /** Fingers draw (setting + Pencil seen): else a finger never selects or moves with the grab tools but Velja. */
  fingersDraw: boolean;
  choosingTarget: boolean;
  textOpen: boolean;
  selectedId: string | null;
  store: OverlayStore;
  pages: readonly PageSize[];
  inkStyle: { color: string; width: number };
  highlightStyle: { color: string; width: number };
  isMine(id: string): boolean;
  getObject(id: string): AnnotationObject | undefined;
  /** Own object top-most at a page point that a tap with the current tool selects (Velja: any), or null. */
  pick(pt: PagePoint): AnnotationObject | null;
  /** Own object top-most at a page point that a long press selects (all but connectors, routing.ts LONG_PRESS_TOLERANCE_PX), or null. */
  pressPick(pt: PagePoint): AnnotationObject | null;
  /** Is the page point on the selection: inside its frame, or on the mark itself (routing.ts onSelectionArea)? */
  inSelection(pt: PagePoint): boolean;
  select(id: string | null): void;
  notePen(): void;
  commitText(): void;
  /** Resolves and performs a tap; 'draw' = a stroke tool keeps what it drew. */
  tap(pt: PagePoint, client: { x: number; y: number }, pointerType: string, from: TapFrom): TapAction;
  commitInk(page: number, pts: RawPt[]): void;
  commitHighlight(page: number, a: Pt, b: Pt): void;
  commitErase(ids: string[], page: number): void;
  /** Returns true when a change was applied. dx/dy normalised. */
  commitMove(id: string, dx: number, dy: number): boolean;
  /** `boxW`: the page box's CSS width (a connector's pin scales from its size as drawn there). */
  commitScale(id: string, origin: Pt, k: number, boxW: number): boolean;
}

interface DragBase {
  pointerId: number;
  type: string;
}
/** A pen / highlighter stroke that may still turn out to be a tap (selects an own text or symbol). */
interface StrokeTap {
  x0: number;
  y0: number;
  t0: number;
  moved: boolean;
  pt: PagePoint;
  /** Selection when it went down: it ends once the stroke travels or is kept (not when it turns into a pan). */
  sel: string | null;
  /** Draw nothing until it travels: a tap here would select or deselect instead of leaving a dot. */
  quiet: boolean;
}
/** Where a drag of the selection went down: one that does not travel acts as a tap there. */
interface GrabTap {
  x0: number;
  y0: number;
  t0: number;
  moved: boolean;
  pt: PagePoint | null;
  /** On a handle's visible icon: a tap there leaves everything as it is. */
  icon: boolean;
  /**
   * Grabbed by a long press: the mark stays put until the pointer leaves the tap slop (a resting
   * Pencil's tremor), and lifting it there only leaves it selected.
   */
  held: boolean;
}
/** Where a pointer is: a PointerEvent, or a long press's last position. */
type PointerAt = Pick<PointerEvent, 'pointerId' | 'pointerType' | 'clientX' | 'clientY'>;
/** A long press waiting for its timer: the pointer, the tool it began with, where it went down and is now. */
interface Press {
  pointerId: number;
  type: string;
  tool: ToolId;
  x0: number;
  y0: number;
  x: number;
  y: number;
  timer: ReturnType<typeof setTimeout>;
}
type Drag =
  | (DragBase & { kind: 'ink'; page: number; rect: DOMRect; H: number; size: number; color: string; pts: RawPt[]; units: number[][]; tap: StrokeTap })
  | (DragBase & { kind: 'highlight'; page: number; rect: DOMRect; aspect: number; H: number; a: Pt; b: Pt; color: string; width: number; tap: StrokeTap })
  | (DragBase & { kind: 'erase'; last: { page: number; u: Pt } | null; ids: Set<string>; pages: Set<number>; page: number })
  | (DragBase & {
      kind: 'move';
      obj: AnnotationObject;
      rect: DOMRect;
      start: Pt;
      dx: number;
      dy: number;
      tap: GrabTap;
    })
  | (DragBase & {
      kind: 'scale';
      obj: AnnotationObject;
      rect: DOMRect;
      H: number;
      originN: Pt;
      originU: Pt;
      handleU: Pt;
      /** Pointer at pointerdown (page units): the handle follows the pointer's travel, not its position. */
      startU: Pt;
      k: number;
      tap: GrabTap;
    })
  | (DragBase & { kind: 'tap'; x0: number; y0: number; t0: number; moved: boolean; pt: PagePoint; from: TapFrom; icon: boolean });

const strokeTapped = (t: StrokeTap): boolean => !t.moved && isTap(0, 0, performance.now() - t.t0);

export function useEditInput(enabled: boolean, handle: ViewerHandle | null, live: RefObject<EditInputLive | null>): void {
  useEffect(() => {
    const el = enabled && handle ? handle.getScrollElement() : null;
    if (!el || !handle) return;
    const L = () => live.current!;

    const pen: PenState = { penDown: false, lastPenUpAt: -Infinity };
    let penPointer: number | null = null;
    const touches = new Map<number, { x: number; y: number }>();
    const consumed = new Set<number>();
    let pan: { last: Pt; c0: Pt; d0: number; d1: number } | null = null;
    let gestureSeen = false;
    let drag: Drag | null = null;
    let raf = 0;
    let press: Press | null = null;
    const clearPress = () => {
      if (press) clearTimeout(press.timer);
      press = null;
    };

    const rectOf = (page: number) => pageElement(el, page)?.getBoundingClientRect() ?? null;

    /* ── rendering of transient state (rAF) ── */

    const render = () => {
      raf = 0;
      const d = drag;
      if (!d) return;
      const lv = L();
      switch (d.kind) {
        case 'ink': {
          if (d.tap.quiet && !d.tap.moved) break;
          const path = lv.store.wet.get(d.page)?.ink;
          if (path) {
            path.setAttribute('d', outlinePath(d.units, d.size, false));
            path.setAttribute('fill', d.color);
          }
          break;
        }
        case 'highlight': {
          if (d.tap.quiet && !d.tap.moved) break;
          const line = lv.store.wet.get(d.page)?.highlight;
          if (line) {
            line.setAttribute('x1', String(d.a.x * UNITS_W));
            line.setAttribute('y1', String(d.a.y * d.H));
            line.setAttribute('x2', String(d.b.x * UNITS_W));
            line.setAttribute('y2', String(d.b.y * d.H));
            line.setAttribute('stroke', d.color);
            line.setAttribute('stroke-width', String((d.width / Q) * UNITS_W));
            line.setAttribute('visibility', 'visible');
          }
          break;
        }
        case 'erase':
          if (d.ids.size !== lv.store.ui.erasing.size) lv.store.setUi({ erasing: new Set(d.ids), erasingPages: new Set(d.pages) });
          break;
        case 'move':
          lv.store.setUi({ preview: { id: d.obj.id, page: d.obj.page, dx: d.dx, dy: d.dy, k: 1, ox: 0, oy: 0 } });
          break;
        case 'scale':
          lv.store.setUi({ preview: { id: d.obj.id, page: d.obj.page, dx: 0, dy: 0, k: d.k, ox: d.originN.x, oy: d.originN.y } });
          break;
        case 'tap':
          break;
      }
    };
    const schedule = () => {
      if (!raf) raf = requestAnimationFrame(render);
    };

    /* ── drags ── */

    const addInkPoint = (d: Extract<Drag, { kind: 'ink' }>, clientX: number, clientY: number, pressure: number) => {
      if (d.pts.length >= MAX_RAW_POINTS) return;
      const p = clientToPage(d.rect, clientX, clientY);
      const u = [p.x * UNITS_W, p.y * d.H];
      const prev = d.units[d.units.length - 1];
      if (prev && prev[0] === u[0] && prev[1] === u[1]) return;
      let pr: number;
      if (d.type === 'pen') pr = penPressure(pressure);
      else if (!prev) pr = FINGER_START_PRESSURE;
      else pr = simulatePressure(prev[2]!, Math.hypot(u[0]! - prev[0]!, u[1]! - prev[1]!), d.size);
      d.pts.push({ x: p.x, y: p.y, p: pr });
      d.units.push([u[0]!, u[1]!, pr]);
    };

    const eraseTo = (d: Extract<Drag, { kind: 'erase' }>, clientX: number, clientY: number) => {
      const lv = L();
      const pt = handle.pointFromClient(clientX, clientY);
      if (!pt) {
        d.last = null;
        return;
      }
      const rect = rectOf(pt.page);
      if (!rect) return;
      const H = pageHeightUnits(lv.pages[pt.page]);
      const u = normToUnits(pt, H);
      const from = d.last && d.last.page === pt.page ? d.last.u : u;
      const tol = cssToUnits(HIT_TOLERANCE_PX, rect.width);
      const ctx = hitContext(H, rect.width);
      for (const o of lv.store.getPage(pt.page)) {
        if (!d.ids.has(o.id) && lv.isMine(o.id) && segmentHitsObject(o, from, u, tol, ctx)) {
          d.ids.add(o.id);
          d.pages.add(pt.page);
        }
      }
      d.last = { page: pt.page, u };
      d.page = pt.page;
    };

    const startMoveOrScale = (e: PointerAt, obj: AnnotationObject, mode: 'move' | 'scale', icon = false, held = false): Drag | null => {
      const rect = rectOf(obj.page);
      if (!rect) return null;
      const lv = L();
      const start = clientToPage(rect, e.clientX, e.clientY, false);
      const tap: GrabTap = { x0: e.clientX, y0: e.clientY, t0: performance.now(), moved: false, pt: handle.pointFromClient(e.clientX, e.clientY), icon, held };
      if (mode === 'move') {
        return { kind: 'move', pointerId: e.pointerId, type: e.pointerType, obj, rect, start, dx: 0, dy: 0, tap };
      }
      const H = pageHeightUnits(lv.pages[obj.page]);
      const b = objectBounds(obj, hitContext(H, rect.width));
      // A connector's pin grows about its centre (`at` and the target never move); the rest about the top-left corner.
      const originU = obj.kind === 'connector' ? { x: (b.x0 + b.x1) / 2, y: (b.y0 + b.y1) / 2 } : { x: b.x0, y: b.y0 };
      return {
        startU: normToUnits(start, H),
        kind: 'scale',
        pointerId: e.pointerId,
        type: e.pointerType,
        obj,
        rect,
        H,
        originU,
        originN: { x: originU.x / UNITS_W, y: originU.y / H },
        handleU: { x: b.x1, y: b.y1 },
        k: 1,
        tap,
      };
    };

    /* ── long press ── */

    /** Arms the long-press timer for the drag / tap this pointerdown just started (routing.ts armsLongPress). */
    const armPress = (e: PointerEvent, route: DownRoute) => {
      const lv = L();
      if (drag?.pointerId !== e.pointerId) return;
      if (!armsLongPress(route, { pointerType: e.pointerType, tool: lv.tool, fingersDraw: lv.fingersDraw, choosingTarget: lv.choosingTarget })) return;
      clearPress();
      const p = { x: e.clientX, y: e.clientY };
      press = { pointerId: e.pointerId, type: e.pointerType, tool: lv.tool, x0: p.x, y0: p.y, ...p, timer: setTimeout(firePress, LONG_PRESS_MS) };
    };

    /** The press stayed still: the own mark under it is selected and the same pointer drags it. */
    const firePress = () => {
      const p = press;
      press = null;
      const d = drag;
      if (!p || !d || d.pointerId !== p.pointerId || (d.kind !== 'tap' && d.kind !== 'ink' && d.kind !== 'highlight')) return;
      const lv = L();
      const hit = lv.pressPick(d.kind === 'tap' ? d.pt : d.tap.pt);
      const action = resolveLongPress({
        armedTool: p.tool,
        tool: lv.tool,
        moved: d.kind === 'tap' ? d.moved : d.tap.moved,
        panning: pan !== null,
        textOpen: lv.textOpen,
        choosingTarget: lv.choosingTarget,
        hit,
      });
      if (action !== 'grab' || !hit) return;
      if (d.kind === 'tap') {
        // A tap drag is not captured: its lift may have landed outside the viewer (on the selection
        // bar, the toolbar) unseen. Capture it now; a pointer that is no longer down cannot be.
        let held = false;
        try {
          el.setPointerCapture(p.pointerId);
          held = el.hasPointerCapture?.(p.pointerId) ?? false;
        } catch {
          /* pointer gone */
        }
        if (!held) {
          drag = null; // a stale tap: drop it, grab nothing
          return;
        }
      }
      const next = startMoveOrScale({ pointerId: p.pointerId, pointerType: p.type, clientX: p.x, clientY: p.y }, hit, 'move', false, true);
      if (!next) return;
      // The stroke, dot or tap the press began as is dropped: nothing saved, no undo step.
      if (d.kind !== 'tap') lv.store.clearWet(d.page);
      drag = next;
      // A finger that grabbed a mark is not a page-turning swipe (two-up, fingers navigate).
      if (p.type === 'touch') handle.cancelSwipe?.();
      lv.select(hit.id);
      try {
        el.setPointerCapture(p.pointerId);
      } catch {
        /* capture unsupported, or the pointer is gone */
      }
      schedule();
    };

    const finish = (commit: boolean) => {
      clearPress();
      const d = drag;
      drag = null;
      if (raf) {
        cancelAnimationFrame(raf);
        raf = 0;
      }
      if (!d) return;
      try {
        if (el.hasPointerCapture?.(d.pointerId)) el.releasePointerCapture(d.pointerId);
      } catch {
        /* pointer already gone */
      }
      const lv = L();
      if (d.kind === 'ink' || d.kind === 'highlight') {
        // A pen / highlighter tap selects an own text or symbol under it, or ends the selection.
        const from = { selectedId: d.tap.sel, onHandle: false, selects: tapMaySelect(d.type, lv.tool, lv.fingersDraw) };
        if (commit && strokeTapped(d.tap) && lv.tap(d.tap.pt, { x: d.tap.x0, y: d.tap.y0 }, d.type, from) !== 'draw') {
          lv.store.clearWet(d.page);
          return;
        }
        // A kept stroke ends the selection (one that travelled already did); a cancelled one keeps it.
        if (commit && d.tap.sel && !d.tap.moved && lv.selectedId === d.tap.sel) lv.select(null);
      }
      switch (d.kind) {
        case 'ink':
          if (commit && d.pts.length > 0) {
            const path = lv.store.wet.get(d.page)?.ink;
            if (path) path.setAttribute('d', outlinePath(d.units, d.size, true));
            lv.commitInk(d.page, d.pts);
          } else lv.store.clearWet(d.page);
          break;
        case 'highlight': {
          const len = Math.hypot((d.b.x - d.a.x) * UNITS_W, (d.b.y - d.a.y) * d.H);
          if (commit && len >= 4) lv.commitHighlight(d.page, d.a, d.b);
          else lv.store.clearWet(d.page);
          break;
        }
        case 'erase':
          if (commit && d.ids.size > 0) lv.commitErase([...d.ids], [...d.pages][0] ?? d.page);
          else lv.store.clearErasing();
          break;
        case 'move':
        case 'scale': {
          const t = d.tap;
          // A quick tap on the move area or a handle (≥ 44 px targets): no nudge. It is a tap on the
          // selection (Texti: edit the text), on another own mark there (select it), or next to it
          // (the tool's tap: a new text / symbol) — except on a handle's visible icon, or where a long
          // press grabbed the mark (it stays selected; Texti does not open its editor).
          if (commit && !t.moved && isTap(0, 0, performance.now() - t.t0)) {
            lv.store.setUi({ preview: null });
            if (t.pt && !t.icon && !t.held) {
              lv.tap(t.pt, { x: t.x0, y: t.y0 }, d.type, { selectedId: d.obj.id, onHandle: true, selects: tapMaySelect(d.type, lv.tool, lv.fingersDraw) });
            }
            break;
          }
          // A finger that never left the tap slop (a resting palm, a long press) changes nothing; nor
          // does a long-press grab lifted inside it (its delta stayed 0).
          const kept =
            commit &&
            (d.type !== 'touch' || t.moved) &&
            (d.kind === 'move'
              ? (d.dx !== 0 || d.dy !== 0) && lv.commitMove(d.obj.id, d.dx, d.dy)
              : Math.abs(d.k - 1) > 0.01 && lv.commitScale(d.obj.id, d.originN, d.k, d.rect.width));
          if (!kept) lv.store.setUi({ preview: null });
          break;
        }
        case 'tap':
          if (commit && !d.moved && !d.icon && isTap(0, 0, performance.now() - d.t0)) lv.tap(d.pt, { x: d.x0, y: d.y0 }, d.type, d.from);
          break;
      }
    };

    /* ── two-finger pan ── */

    const centroid = (): { c: Pt; d: number } | null => {
      const pts = [...touches.values()];
      if (pts.length < 2) return null;
      const a = pts[0]!;
      const b = pts[1]!;
      return { c: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }, d: Math.hypot(a.x - b.x, a.y - b.y) || 1 };
    };
    const startPan = () => {
      if (drag && drag.type === 'touch') finish(false); // a finger stroke turned into a pan: discard it
      for (const id of touches.keys()) consumed.add(id);
      const g = centroid();
      if (!g) return;
      pan = { last: g.c, c0: g.c, d0: g.d, d1: g.d };
    };
    const updatePan = () => {
      const g = centroid();
      if (!pan || !g) return;
      el.scrollLeft -= g.c.x - pan.last.x;
      el.scrollTop -= g.c.y - pan.last.y;
      pan.last = g.c;
      pan.d1 = g.d;
    };
    const endPan = () => {
      const p = pan;
      pan = null;
      if (!p) return;
      const k = p.d1 / p.d0;
      // WebKit normally zooms through gesture events (viewer); commit here only if none arrived.
      // The flag is read and cleared only here: a gesturestart may arrive before the second finger's
      // pointerdown, and a third finger restarts the pan in the middle of the same gesture.
      const seen = gestureSeen;
      gestureSeen = false;
      if (supportsGestureEvents() && !seen && Math.abs(k - 1) > 0.08) {
        handle.setZoom(handle.getZoom() * k, { clientX: p.c0.x, clientY: p.c0.y });
      }
    };

    /* ── pointer events ── */

    const onDown = (e: PointerEvent) => {
      const target = e.target instanceof Element ? e.target : null;
      if (!target || target.closest(INTERACTIVE_SELECTOR)) return;
      const lv = L();
      const now = performance.now();
      const type = e.pointerType;
      if (type === 'pen') {
        pen.penDown = true;
        penPointer = e.pointerId;
        lv.notePen();
        if (drag && drag.type === 'touch') finish(false); // the palm got there first
      }
      if (type === 'touch') {
        if (palmRejected(type, pen, now)) {
          consumed.add(e.pointerId);
          return;
        }
        // The primary touch starts a new sequence: no other finger is down, so drop entries whose
        // pointerup never reached the scroller (they would make every later tap look like a pinch).
        if (e.isPrimary && touches.size > 0) {
          touches.clear();
          pan = null;
        }
        // First finger of a new touch sequence: forget a gesture flag left by an earlier gesture.
        if (touches.size === 0 && !pan) gestureSeen = false;
        touches.set(e.pointerId, { x: e.clientX, y: e.clientY });
        if (touches.size >= 2) {
          // Two fingers pan / zoom. A one-finger drag of the selection is dropped (it would land
          // where the pinch left it); the JS pan takes over when fingers draw or that finger was on
          // the selection (touch-action:none there: the browser would not pan).
          const grabbing = drag?.type === 'touch' && (drag.kind === 'move' || drag.kind === 'scale');
          if (lv.role === 'draw' || grabbing) {
            startPan();
            return;
          }
          if (drag?.type === 'touch') finish(false); // a tap that turned into a native pan / pinch
          consumed.add(e.pointerId);
          return;
        }
        if (consumed.has(e.pointerId)) return;
      }
      if (drag?.kind === 'tap') {
        drag = null; // a tap whose pointerup landed outside the viewer
        clearPress();
      }
      if (drag) return; // one drawing pointer at a time
      if (!target.closest('[data-page]')) return;
      if (lv.textOpen) {
        lv.commitText();
        if (type === 'touch') consumed.add(e.pointerId);
        return;
      }
      const pt = handle.pointFromClient(e.clientX, e.clientY);
      const sel = lv.selectedId ? lv.getObject(lv.selectedId) : undefined;
      // On the selection: its resize handle, move grip, move area (≥ 44 pt) or inside its frame —
      // the frame margin only for pointers that cannot scroll the page there (a scrolling finger
      // would be cancelled mid-drag; its tap inside the frame still counts as on the selection).
      // Ink / a highlight in Penni / Yfirstrikun: its move area counts only on the mark itself
      // (routing.ts selectionGrabbedOnMark, inSelection); elsewhere in its frame the tool writes.
      let on: SelectionPart | null = null;
      let icon = false;
      if (sel) {
        const handleEl = target.closest<HTMLElement>('[data-annot-handle]');
        const handleKind = handleEl?.dataset.annotHandle;
        if (handleEl && (handleKind === 'grip' || handleKind === 'scale')) {
          const r = handleEl.getBoundingClientRect();
          icon = Math.hypot(e.clientX - (r.left + r.width / 2), e.clientY - (r.top + r.height / 2)) <= HANDLE_ICON_RADIUS_PX;
        }
        if (handleKind === 'scale') on = 'scale';
        else if (handleKind === 'grip' || (handleKind === 'move' && !selectionGrabbedOnMark(sel.kind, lv.tool))) on = 'move';
        else if (pt && (type !== 'touch' || lv.role === 'draw') && lv.inSelection(pt)) on = 'move';
      }
      const route = routePointerDown({
        pointerType: type,
        buttons: e.buttons,
        tool: lv.tool,
        role: lv.role,
        fingersDraw: lv.fingersDraw,
        pen,
        now,
        on,
        scalable: !!sel && selectionScalable(sel.kind, lv.tool),
        choosingTarget: lv.choosingTarget,
      });
      if (route === 'ignore') return;
      if (route === 'tap') {
        if (pt) {
          drag = {
            kind: 'tap',
            pointerId: e.pointerId,
            type,
            x0: e.clientX,
            y0: e.clientY,
            t0: now,
            moved: false,
            pt,
            from: { selectedId: lv.selectedId, onHandle: on !== null, selects: tapMaySelect(type, lv.tool, lv.fingersDraw) },
            icon,
          };
          armPress(e, route);
        }
        return;
      }

      let next: Drag | null = null;
      if ((route === 'move' || route === 'scale') && sel) {
        next = startMoveOrScale(e, sel, route, icon);
      } else if (route === 'pick-move') {
        if (pt) {
          const hit = lv.pick(pt);
          lv.select(hit?.id ?? null);
          if (hit) next = startMoveOrScale(e, hit, 'move');
        }
      } else if (route === 'stroke' && pt) {
        const rect = rectOf(pt.page);
        const size = lv.pages[pt.page];
        if (!rect) return;
        const H = pageHeightUnits(size);
        // Drawing elsewhere ends the selection once the stroke travels or is kept (finish); a tap may
        // still select an own text / symbol, and a stroke that turns into a two-finger pan keeps it.
        const selAtDown = lv.selectedId;
        const strokeTap = (): StrokeTap => ({
          x0: e.clientX,
          y0: e.clientY,
          t0: now,
          moved: false,
          pt,
          sel: selAtDown,
          quiet: selAtDown !== null || lv.pick(pt) !== null,
        });
        // A new stroke owns the wet layer: drop any wet preview still waiting for its object.
        if (lv.tool === 'pen' || lv.tool === 'highlighter') lv.store.releaseWet();
        if (lv.tool === 'pen') {
          const d: Drag = {
            kind: 'ink',
            pointerId: e.pointerId,
            type,
            page: pt.page,
            rect,
            H,
            size: (lv.inkStyle.width / Q) * UNITS_W,
            color: lv.inkStyle.color,
            pts: [],
            units: [],
            tap: strokeTap(),
          };
          addInkPoint(d, e.clientX, e.clientY, e.pressure);
          next = d;
        } else if (lv.tool === 'highlighter') {
          const a = { x: pt.x, y: pt.y };
          next = {
            kind: 'highlight',
            pointerId: e.pointerId,
            type,
            page: pt.page,
            rect,
            H,
            aspect: H / UNITS_W,
            a,
            b: a,
            color: lv.highlightStyle.color,
            width: lv.highlightStyle.width,
            tap: strokeTap(),
          };
        } else if (lv.tool === 'eraser') {
          const d: Drag = { kind: 'erase', pointerId: e.pointerId, type, last: null, ids: new Set(), pages: new Set(), page: pt.page };
          eraseTo(d, e.clientX, e.clientY);
          next = d;
        }
      }
      if (!next) return;
      drag = next;
      try {
        el.setPointerCapture(e.pointerId);
      } catch {
        /* capture unsupported */
      }
      armPress(e, route);
      schedule();
    };

    const onMove = (e: PointerEvent) => {
      if (e.pointerType === 'touch' && touches.has(e.pointerId)) {
        touches.set(e.pointerId, { x: e.clientX, y: e.clientY });
        if (pan) {
          updatePan();
          return;
        }
      }
      const d = drag;
      if (!d || d.pointerId !== e.pointerId) return;
      if (press?.pointerId === e.pointerId) {
        // A long press must keep still: it follows a resting Pencil's tremor, and any drift beyond
        // that (a slowly drawn dot, tick or breath mark) ends it, so the stroke is kept.
        if (Math.hypot(e.clientX - press.x0, e.clientY - press.y0) <= longPressSlopPx(press.type)) {
          press.x = e.clientX;
          press.y = e.clientY;
        } else clearPress();
      }
      if (d.kind === 'tap') {
        if (Math.hypot(e.clientX - d.x0, e.clientY - d.y0) > 10) d.moved = true;
        return;
      }
      // A pen/mouse that is no longer pressed (lifted without pointerup, or hovering) ends the drag.
      if ((e.pointerType === 'pen' || e.pointerType === 'mouse') && (e.buttons & 1) === 0) {
        finish(true);
        return;
      }
      if ((d.kind === 'ink' || d.kind === 'highlight') && !d.tap.moved && !isTap(e.clientX - d.tap.x0, e.clientY - d.tap.y0, 0)) {
        d.tap.moved = true;
        const lv = L();
        if (d.tap.sel && lv.selectedId === d.tap.sel) lv.select(null);
      }
      const coalesced = typeof e.getCoalescedEvents === 'function' ? e.getCoalescedEvents() : [];
      const events = coalesced.length > 0 ? coalesced : [e];
      switch (d.kind) {
        case 'ink':
          for (const ce of events) addInkPoint(d, ce.clientX, ce.clientY, ce.pressure > 0 ? ce.pressure : e.pressure);
          break;
        case 'highlight':
          d.b = snapHorizontal(d.a, clientToPage(d.rect, e.clientX, e.clientY), d.aspect);
          break;
        case 'erase':
          for (const ce of events) eraseTo(d, ce.clientX, ce.clientY);
          break;
        case 'move': {
          if (!d.tap.moved && !isTap(e.clientX - d.tap.x0, e.clientY - d.tap.y0, 0)) d.tap.moved = true;
          if (d.tap.held && !d.tap.moved) break;
          const p = clientToPage(d.rect, e.clientX, e.clientY, false);
          const c = clampDelta(d.obj, Math.round((p.x - d.start.x) * Q), Math.round((p.y - d.start.y) * Q));
          d.dx = c.dx / Q;
          d.dy = c.dy / Q;
          break;
        }
        case 'scale': {
          if (!d.tap.moved && !isTap(e.clientX - d.tap.x0, e.clientY - d.tap.y0, 0)) d.tap.moved = true;
          const u = normToUnits(clientToPage(d.rect, e.clientX, e.clientY, false), d.H);
          d.k = dragScale(d.originU, d.handleU, { x: d.handleU.x + u.x - d.startU.x, y: d.handleU.y + u.y - d.startU.y });
          break;
        }
      }
      schedule();
    };

    const onEnd = (e: PointerEvent, cancelled: boolean) => {
      if (e.pointerType === 'pen' && e.pointerId === penPointer) {
        pen.penDown = false;
        pen.lastPenUpAt = performance.now();
        penPointer = null;
      }
      if (e.pointerType === 'touch') {
        touches.delete(e.pointerId);
        consumed.delete(e.pointerId);
        if (pan && touches.size < 2) endPan();
      }
      const d = drag;
      if (!d || d.pointerId !== e.pointerId) return;
      // pointercancel ends a stroke like a lift; only a tap is dropped (the finger started scrolling).
      finish(d.kind === 'tap' ? !cancelled : true);
    };
    const onUp = (e: PointerEvent) => onEnd(e, false);
    const onCancel = (e: PointerEvent) => onEnd(e, true);

    /* ── native touch blocking ── */

    const shouldBlock = (e: TouchEvent): boolean => {
      const target = e.target instanceof Element ? e.target : null;
      if (!target || !target.closest('[data-page]') || target.closest(INTERACTIVE_SELECTOR)) return false;
      // Fingers draw, the JS pan runs, or a pen is writing / just lifted (palm): no native scrolling.
      if (L().role === 'draw' || pan || palmRejected('touch', pen, performance.now())) return true;
      // A finger that grabbed a mark by a long press (Texti / Tákn, fingers navigate) drags it.
      if (drag?.type === 'touch' && drag.kind === 'move' && drag.tap.held) return true;
      for (let i = 0; i < e.changedTouches.length; i++) {
        if ((e.changedTouches[i] as Touch & { touchType?: string }).touchType === 'stylus') return true;
      }
      return false;
    };
    const onTouch = (e: TouchEvent) => {
      if (e.cancelable && shouldBlock(e)) e.preventDefault();
    };
    const onContextMenu = (e: Event) => {
      const target = e.target instanceof Element ? e.target : null;
      if (target?.closest('[data-page]') && !target.closest(INTERACTIVE_SELECTOR)) e.preventDefault();
    };
    const onGesture = () => {
      gestureSeen = true;
    };

    const active: AddEventListenerOptions = { passive: false };
    el.addEventListener('touchstart', onTouch, active);
    el.addEventListener('touchmove', onTouch, active);
    el.addEventListener('pointerdown', onDown);
    el.addEventListener('pointermove', onMove);
    el.addEventListener('pointerup', onUp);
    el.addEventListener('pointercancel', onCancel);
    el.addEventListener('contextmenu', onContextMenu);
    el.addEventListener('selectstart', onContextMenu);
    el.addEventListener('gesturestart', onGesture, { passive: true });
    return () => {
      el.removeEventListener('touchstart', onTouch);
      el.removeEventListener('touchmove', onTouch);
      el.removeEventListener('pointerdown', onDown);
      el.removeEventListener('pointermove', onMove);
      el.removeEventListener('pointerup', onUp);
      el.removeEventListener('pointercancel', onCancel);
      el.removeEventListener('contextmenu', onContextMenu);
      el.removeEventListener('selectstart', onContextMenu);
      el.removeEventListener('gesturestart', onGesture);
      finish(false);
    };
  }, [enabled, handle, live]);
}
